import * as amplitude from '@amplitude/analytics-browser';
import { Experiment } from '@amplitude/experiment-js-client';
import { getConfig } from './configuration';
import gaToAmplitudeNamingPluginConstructor from './plugins/gaToAmplitudeNamingPlugin';
import namingRemapingPluginConstructor from './plugins/namingRemappingPlugin';
import globalEventPropertiesPluginConstructor from './plugins/globalEventPropertiesPlugin';
import * as cookies from './cookies';

// TODO: Core config for init.

/**
 * Initialises Amplitude using core config.
 *
 * @property codeSource - A unique identifier for this code. The GitHub id, e.g. aw-dynamic-web, plus platform suffix where multiple UMs or Amplitude SDKs exist in one repo.
 * @property codeVersion - A version string of the code.
 * @property userId - The FanDuel user ID.  If logged out pass `undefined`.
 * @property deviceId - The Amplitude device ID, if known.  (Used for maintaining session consistency between Amplitude instances.)
 * @property sessionId - The Amplitude session ID, if known.  (Used for maintaining session consistency between Amplitude instances.)
 * @property remapGaNaming - Enable the programmatic remapping of GA naming to Amplitude naming.
 * @property remapNames - Enable the programmatic remapping of names within events.
 * @property globalEventProperties - Static event properties to send with *every* event (e.g. app version number).
 * @property globalEventPropertiesCallback - Callback to generate event properties to send with *every* event.
 * @property requiredEventPropertiesCallback?: RequiredEventPropertiesCallback - Required properties callback, required by UXAE team. Optional as already implemented in some codebases using globalEventPropertiesCallback.
 * @property debug - Enable verbose console logging from Amplitude.
 * @property enableExperiment - Enable the Experiment SDK with Analytics.
 * @property experimentAutoExposure - pass `false` to disable Experiment auto exposure events.
 * @property experimentDeployment - pass in the name of the deployment key for the experiments. Default is 'web'
 * @property killSwitch - pass `true` to completely disable Amplitude.
 * @property stateIdCookieHandling - pass 'true' to read and write the amp_device_id cookie for cross sell tracking.
 * @property automaticGlobalProperties - use window.location.href and country to populate some required global properties.
 */

/**
 * Initialises Amplitude without core config.
 *
 * @property product - The FanDuel product, e.g. 'sportsbook', 'dfs'.
 * @property environment - The FanDuel environment, e.g. 'prod', 'test', 'dev'.
 * @property country - The country, either 'usa' or 'canada'
 */

export const defaultVariant = {
  value: 'off',
  payload: undefined
};
let enabled = true; // Turn on/off *all* calls to Amplitude.
let experiment; // Experiment singleton instance.
let autoExposure; // Are we using auto exposure events?
let initialiser; // Initialiser promise singleton.
let debug; // whether to do debug logging.

// Wraps calls to amplitude to allow central control.
const wrappedAnalyticsCall = (method, ...args) => {
  if (enabled) {
    return method(...args);
  } else {
    return {
      promise: new Promise(resolve => resolve({
        code: 200,
        event: {},
        message: 'Analytics not enabled'
      }))
    };
  }
};
export const initWithConfig = config => {
  const {
    codeSource,
    codeVersion,
    userId,
    product,
    environment,
    country = 'usa',
    remapGaNaming,
    remapNames,
    globalEventProperties,
    globalEventPropertiesCallback,
    requiredEventPropertiesCallback,
    enableExperiment = false,
    experimentAutoExposure = true,
    experimentDeployment,
    killSwitch = false,
    stateIdCookieHandling = false,
    automaticGlobalProperties = false
  } = config;
  debug = config.debug;
  if (debug) {
    writeConfig();
  }
  if (!initialiser) {
    if (killSwitch) {
      enabled = false;
      initialiser = new Promise(resolve => {
        resolve({
          deviceId: undefined,
          sessionId: undefined
        });
      });
    } else {
      const {
        deviceId,
        sessionId
      } = cookies.getStoredCookies({
        deviceId: config.deviceId,
        sessionId: config.sessionId,
        stateIdCookieHandling
      });
      const amplitudeConfig = getConfig(product, environment, country, experimentDeployment, debug);
      const amplitudeResult = wrappedAnalyticsCall(amplitude.init, amplitudeConfig.apiKey, userId, amplitudeConfig.amplitudeOptions).promise;
      initialiser = new Promise((resolve, reject) => {
        if (remapNames) {
          amplitude.add(namingRemapingPluginConstructor(remapNames));
        }
        if (remapGaNaming) {
          amplitude.add(gaToAmplitudeNamingPluginConstructor(remapNames));
        }
        if (globalEventProperties || globalEventPropertiesCallback || requiredEventPropertiesCallback) {
          const globalProperties = automaticGlobalProperties ? {
            'National Jurisdiction': country,
            ...globalEventProperties
          } : globalEventProperties || {};
          amplitude.add(globalEventPropertiesPluginConstructor(automaticGlobalProperties, globalProperties, globalEventPropertiesCallback, requiredEventPropertiesCallback));
        }
        deviceId && setDeviceId(deviceId);
        sessionId && setSessionId(sessionId);
        amplitudeResult.then(async () => {
          log('Debug Core Analytics Module Initialised', {
            'Debug Product': product,
            'Debug Environment': environment,
            'Debug Country': country,
            'Debug Experiment Enabled': !!enableExperiment,
            'Debug Experiment Deployment': experimentDeployment || 'unset',
            'Debug Experiment Auto-exposure': !!experimentAutoExposure,
            'Debug Debug': !!debug,
            'Debug Code Source': codeSource,
            'Debug Code Version': codeVersion ?? '',
            'Debug UM Version': '4.4.4',
            'Debug Config Device Id': config.deviceId ?? '',
            'Debug Final Device Id': deviceId ?? '',
            'Debug Config Session Id': config.sessionId ?? '',
            'Debug Final Session Id': sessionId ?? ''
          });
          if (enableExperiment) {
            // Exposure tracking provider?
            _initExperiment(amplitudeConfig.experimentKey, amplitudeConfig.experimentOptions, experimentAutoExposure);
            // Fetch experiments from Amplitude and cache them ready for use.
            await loadExperiments();
          }
          resolve({
            deviceId: getDeviceId(),
            sessionId: getSessionId()
          });
        }).catch(error => {
          if (debug) {
            console.error('Amplitude failed to initialise', error);
          }
          reject(error);
        });
      });
    }
  }
  return initialiser;
  function writeConfig() {
    const {
      deviceId,
      sessionId
    } = config;
    console.info('Config options', JSON.stringify({
      userId,
      deviceId,
      sessionId,
      product,
      environment,
      remapGaNaming,
      remapNames,
      globalEventProperties,
      globalEventPropertiesCallback: globalEventPropertiesCallback ? 'exists' : 'undefined',
      requiredEventPropertiesCallback: requiredEventPropertiesCallback ? 'exists' : 'undefined',
      debug,
      enableExperiment,
      experimentAutoExposure,
      experimentDeployment,
      killSwitch,
      stateIdCookieHandling,
      automaticGlobalProperties
    }, undefined, 2));
  }
};

// @ts-ignore
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const init = config => {
  throw {
    name: 'NotImplementedError',
    message: 'This is not yet implemented.'
  };
  // TODO: Get product and environment from core config.
  /*
  const product = 'dfs';
  const environment = 'dev';
  return initWithConfig({ product, environment, ...config });
  */
};

// Returns the init promise which resolves when the SDK is ready and
// if enables, experiments have been loaded.
export const whenReady = () => {
  return initialiser;
};

// This allows us to set up the experiment singleton for testing.
export const _initExperiment = (key, options, experimentAutoExposure) => {
  autoExposure = experimentAutoExposure;
  experiment = Experiment.initializeWithAmplitudeAnalytics(key, options);
};

/* Analytics */

export const setUserId = userId => {
  return wrappedAnalyticsCall(amplitude.setUserId, userId);
};
export const setDeviceId = deviceId => {
  cookies.setDeviceId(deviceId);
  return wrappedAnalyticsCall(amplitude.setDeviceId, deviceId);
};
export const getDeviceId = () => {
  return enabled ? amplitude.getDeviceId() : undefined;
};
export const setSessionId = sessionId => {
  cookies.setSessionId(sessionId);
  return wrappedAnalyticsCall(amplitude.setSessionId, sessionId);
};
export const getSessionId = () => {
  return enabled ? amplitude.getSessionId() : undefined;
};
export const setTransport = transport => {
  return wrappedAnalyticsCall(amplitude.setTransport, transport);
};
export const log = (eventName, eventProperties, userProperties) => {
  const event = {
    event_type: eventName,
    event_properties: eventProperties,
    user_properties: userProperties
  };
  const result = wrappedAnalyticsCall(amplitude.track, event);
  const sessionId = amplitude.getSessionId();
  sessionId && cookies.setSessionId(sessionId);
  return result;
};
export const flush = () => {
  return wrappedAnalyticsCall(amplitude.flush);
};
export const resetSession = () => {
  if (enabled) {
    var _experiment;
    (_experiment = experiment) === null || _experiment === void 0 || _experiment.clear();
    amplitude.reset();
    cookies.setDeviceId(amplitude.getDeviceId());
    cookies.setSessionId(amplitude.getSessionId());
  }
};
export const setUserProperty = (property, value) => {
  return setMultipleUserProperties({
    [property]: value
  });
};
export const setUserPropertyOnce = (property, value) => {
  const identifyEvent = new amplitude.Identify();
  identifyEvent.setOnce(property, value);
  return wrappedAnalyticsCall(amplitude.identify, identifyEvent);
};
export const setMultipleUserProperties = properties => {
  const identifyEvent = new amplitude.Identify();
  for (const [property, value] of Object.entries(properties)) {
    identifyEvent.set(property, value);
  }
  return wrappedAnalyticsCall(amplitude.identify, identifyEvent);
};
export const unsetUserProperty = property => {
  const identifyEvent = new amplitude.Identify();
  identifyEvent.unset(property);
  return wrappedAnalyticsCall(amplitude.identify, identifyEvent);
};

/* Experiment */

export const loadExperiments = async (userId, userProperties) => {
  if (experiment) {
    await experiment.fetch({
      user_id: userId,
      user_properties: userProperties
    });
  }
};
export const getAllExperiments = () => {
  return experiment ? experiment.all() : {};
};
export const getExperimentVariant = (experimentKey, autoExpose, fallback) => {
  fallback = fallback || defaultVariant;
  if (typeof autoExpose === 'undefined') {
    // Use the module default.
    autoExpose = autoExposure;
  }
  if (experiment) {
    const result = experiment.variant(experimentKey, fallback);
    if (autoExpose) {
      exposeExperiment(experimentKey);
    }
    return result;
  } else {
    return fallback;
  }
};

// Experiment automagically populates the variant based on
// what would be returned by a call to `variant(experimentKey)`.
export const exposeExperiment = experimentKey => {
  if (experiment) {
    var _experiment$all;
    if ((_experiment$all = experiment.all()) !== null && _experiment$all !== void 0 && _experiment$all[experimentKey]) {
      experiment.exposure(experimentKey);
    } else {
      if (debug) {
        console.warn('Experiment exposed which is not turned on:', experimentKey);
      }
    }
  }
};
export const cleanProperties = props => {
  if (!props) return props;
  const propsArray = Object.entries(props).filter(([_, v]) => isDefined(v));
  return Object.fromEntries(propsArray);
  function isDefined(value) {
    return value !== undefined && value !== null;
  }
};
export default {};
//# sourceMappingURL=index.js.map